/* Phase 68 — localStorage-backed recent-selection tracker.
 *
 * Stores the last N picked values for a given "scope" (e.g. "brand", "model:BMW").
 * Used by SearchableCombobox + BrandModelSelect to show "Zuletzt" at the top.
 *
 * Storage key format: ch.recent.<scope> => JSON.stringify(string[])
 *
 * Robust to localStorage being unavailable (SSR / disabled): all ops are no-ops.
 */
const STORAGE_KEY = (scope) => `ch.recent.${scope}`;
const MAX_RECENT = 5;

function safeGet(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function safeSet(key, val) {
  try { window.localStorage.setItem(key, val); } catch { /* ignore */ }
}

export function getRecent(scope) {
  if (!scope) return [];
  const raw = safeGet(STORAGE_KEY(scope));
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((v) => typeof v === "string") : [];
  } catch { return []; }
}

export function pushRecent(scope, value) {
  if (!scope || !value || typeof value !== "string") return;
  const cur = getRecent(scope);
  const next = [value, ...cur.filter((v) => v !== value)].slice(0, MAX_RECENT);
  safeSet(STORAGE_KEY(scope), JSON.stringify(next));
}

export function clearRecent(scope) {
  if (!scope) return;
  safeSet(STORAGE_KEY(scope), JSON.stringify([]));
}

// Globally curated "popular" lists (Swiss used-car market intuition).
// These are shown above the alphabetical full list when no recent items exist.
export const POPULAR_BRANDS_CH = [
  "BMW", "Mercedes-Benz", "Audi", "Volkswagen", "Toyota", "Tesla", "Porsche", "Volvo",
];

// Popular models within a brand (top 3–5). Shown above the full model list.
export const POPULAR_MODELS_BY_BRAND = {
  "BMW": ["3er", "5er", "X3", "X5", "i4"],
  "Mercedes-Benz": ["C-Klasse", "E-Klasse", "GLC", "GLE", "A-Klasse"],
  "Audi": ["A3", "A4", "A6", "Q3", "Q5"],
  "Volkswagen": ["Golf", "Tiguan", "Passat", "Polo", "T-Roc"],
  "Toyota": ["Yaris", "Corolla", "RAV4", "Yaris Cross", "C-HR"],
  "Tesla": ["Model 3", "Model Y", "Model S", "Model X"],
  "Porsche": ["911", "Cayenne", "Macan", "Taycan", "Panamera"],
  "Volvo": ["XC60", "XC90", "XC40", "V60", "S60"],
  "Hyundai": ["Tucson", "Kona", "i30", "IONIQ 5", "Santa Fe"],
  "Kia": ["Sportage", "Ceed", "Niro", "EV6", "Sorento"],
  "Skoda": ["Octavia", "Fabia", "Kodiaq", "Karoq", "Superb"],
  "Ford": ["Focus", "Kuga", "Puma", "Fiesta", "Mustang"],
  "Renault": ["Clio", "Captur", "Megane", "Arkana", "Zoe"],
  "Peugeot": ["208", "2008", "308", "3008", "5008"],
  "Fiat": ["500", "Panda", "500X", "Tipo", "Punto"],
};
